/**
 * Video Editor — Learn-Mode inline tips.
 *
 * Covers `routes/social/VideoEditor.tsx` and `components/video-editor/*`.
 */

import type { LearnTip } from "./types";

export const videoEditorTips: LearnTip[] = [
  {
    key: "quick-reel",
    title: "Quick Reel",
    body: "Pick a clip and the part you want, choose the network and caption style, and our servers cut, frame and caption the reel — the finished file is exactly what gets posted.",
  },
  {
    key: "quick-reel-client",
    title: "Client",
    body: "Attributes the reel to a client brand — needed to browse their footage library below, and to make the finished reel a reviewable draft that client can be sent a no-login review link for.",
  },
  {
    key: "quick-reel-footage-source",
    title: "Footage library",
    body: "Reuse a clip already indexed for this client instead of uploading it again — only footage cleared for organic use shows up here.",
  },
  {
    key: "quick-reel-platform",
    title: "Network",
    body: "Checks the finished reel against that network's own length, size and aspect limits before you post it. Leave this on \"general\" if you're not sure yet.",
  },
  {
    key: "quick-reel-framing",
    title: "Framing",
    body: "How the reel crops to fill its shape. Smart crop follows the main subject's face; Speakers switches the crop between whoever's talking when 2 or more people are on camera — it uses the whole clip and skips the in/out trim.",
  },
  {
    key: "quick-reel-framing-mode",
    title: "Reframe motion",
    body: "How a Smart crop moves. Static locks one crop for the whole reel — best for footage that's already edited, with no jitter. Smooth follow keeps the subject centred as they move — best for talking heads.",
  },
  {
    key: "quick-reel-clean-audio",
    title: "Clean up audio",
    body: "Runs the clip's own audio through denoise + loudness normalization before the reel is captioned and cut. Skips silently if it can't improve anything.",
  },
  {
    key: "quick-reel-emphasis",
    title: "Emphasis words",
    body: "Words in this list get a visual pop in the burned-in captions wherever they're spoken — useful for a price, a deadline, or a call to action.",
  },
  {
    key: "quick-reel-find-clips",
    title: "Find clips",
    body: "Scans the selected clip's transcript for moments worth turning into a reel — a triage pass, never a prediction of how it'll perform. Pick one to pre-fill the in/out above.",
  },
  {
    key: "quick-reel-run-qa",
    title: "Run QA",
    body: "Runs the same automated QA pass as the full editor — platform spec checks plus an AI visual review — against this reel.",
  },
  {
    key: "quick-reel-client-review",
    title: "Send for client review",
    body: "Creates a no-login link your client can open to approve, request changes, or comment on this reel.",
  },
  {
    key: "video-editor",
    title: "Video Editor",
    body: "A timeline-based editor for building short-form video — add clips, music, text, and captions, then export for social.",
  },
  {
    key: "video-editor-media",
    title: "Media",
    body: "Add video, image, or music files to this project. Drag and drop files here, or click to choose them from your device.",
  },
  {
    key: "video-editor-preview",
    title: "Preview",
    body: "Shows exactly how the video will look at the selected aspect ratio. Add a clip to the timeline to start editing.",
  },
  {
    key: "video-editor-framing",
    title: "Framing",
    body: "Controls how a clip fits its frame — “Fill” crops the clip to fill the whole frame, “Fit” shows the whole clip with letterboxing if needed.",
  },
  {
    key: "video-editor-motion",
    title: "Motion",
    body: "Adds a punch-in zoom across the clip — an eased zoom from one size to another, centered on the clip's focus point. None/Subtle/Strong are presets; set your own From/To % for a custom zoom.",
  },
  {
    key: "video-editor-speed-ramp",
    title: "Speed ramp",
    body: 'Set a "Ramp to" speed and the clip starts at the speed set above, then eases smoothly to this one by the end — a slow-mo pour that speeds up, or a fast build that lands slow. Leave it Off for a steady speed.',
    bullets: ["A ramped clip's own sound is muted in the export, because audio can't follow a changing speed."],
  },
  {
    key: "video-editor-fades",
    title: "Fades",
    body: "Fades the whole video in from, and out to, black or white — picture and sound together. Text and captions fade with it.",
  },
  {
    key: "video-editor-clip-fades",
    title: "Clip fades",
    body: 'Fades just this clip in or out, so it eases into the next one instead of hard-cutting. Tick "Fade the clip\'s audio too" to fade its sound with the picture.',
  },
  {
    key: "video-editor-text",
    title: "Text",
    body: "Add a text overlay to the video, starting from a preset style or a blank text box.",
  },
  {
    key: "video-editor-captions",
    title: "Captions",
    body: "Generate captions for the video and choose a caption style. Most people watch with sound off, so captions keep them watching and make the video accessible.",
  },
  {
    key: "video-editor-audio",
    title: "Audio",
    body: "Control the original audio from your clips, and add a separate music track on top.",
  },
  {
    key: "video-editor-timeline",
    title: "Timeline",
    body: "Your clips laid out in sequence. Split a clip at the playhead, trim, reorder, or delete clips, and drag the playhead to scrub through the video.",
  },
  {
    key: "video-editor-export",
    title: "Export Video",
    body: "Renders the finished video for download or to attach directly to a post.",
  },
  {
    key: "video-editor-pip",
    title: "Picture-in-Picture",
    body: "Plays a second clip or image on top of the main video — useful for a reaction cam, a logo bug, or a product inset.",
  },
  {
    key: "video-editor-transcript",
    title: "Transcript",
    body: "Edit the video by editing its words — select words to cut, jump to fillers or long pauses, and the timeline ripples automatically.",
  },
  {
    key: "video-editor-assistant",
    title: "Assistant",
    body: 'Describe an edit in plain language — "cut the first 2 seconds", "add a title at the top" — and it\'s applied as one undoable change.',
  },
  {
    key: "video-editor-review",
    title: "Review",
    body: "Every server render becomes a version here, with timecoded comments and approve / request-changes — share a no-login link so a client can weigh in.",
  },
  {
    key: "video-editor-server-render",
    title: "Render",
    body: "Renders on our GPU server — faster than this browser, no size limits, and it keeps going even if you close this tab. The finished file lands under Review.",
  },
  {
    key: "video-editor-library-tab",
    title: "Library",
    body: "Insert a clip straight from the client's indexed footage library — no re-upload. Only footage cleared for organic use shows up here.",
  },
  {
    key: "video-editor-music-shelf",
    title: "Brand music",
    body: "Pick a licensed background track instead of uploading your own — already cleared for this client, with a mood filter and preview before you add it.",
  },
  {
    key: "video-editor-sfx-shelf",
    title: "Sound effects",
    body: "Add a whoosh, pop, riser, or impact at the playhead — all license-free, no clearance needed. A whoosh lands on a transition; a riser resolves into an impact.",
  },
  {
    key: "video-editor-sticker-suggest",
    title: "Suggest stickers",
    body: "Reads this video's own captions and proposes emoji stickers timed to matching words. Nothing is added until you tick the ones you want and press Apply.",
  },
  {
    key: "video-editor-cut-to-beat",
    title: "Cut to the beat",
    body: "Proposes cut points on the music track's beats so clip changes land on the rhythm. Pick how often to cut, review the proposal, then apply it as one undoable edit or dismiss it. Needs a music track first.",
  },
  {
    key: "video-editor-story-template",
    title: "Arrange as story",
    body: "Proposes a hook → problem → result → call-to-action order for your clips. Review the proposed arrangement before applying it — nothing changes until you accept.",
  },
  {
    key: "video-editor-gif-stickers",
    title: "GIFs & stickers",
    body: "Search GIPHY or browse what's trending and click a GIF or sticker to drop it at the playhead. It animates in the preview and in the exported video; drag the sliders to size, place, and time it.",
    bullets: [
      "Content is capped at PG rating.",
      "GIPHY media is for organic posts only — don't use the video in ads or boosted posts.",
      "Tiles marked Licensed character or Celebrity are listed last — avoid them in client posts.",
    ],
  },
  {
    key: "gif-picker-ip-risk-badge",
    title: "Licensed character / Celebrity",
    body: "This GIF or sticker shows a studio, TV or film character (SpongeBob, Disney, Marvel…) or a real celebrity. GIPHY lets you search it, but that doesn't license a brand to post it — avoid it in client posts.",
    bullets: [
      "Flagged items still work and are never hidden — they're just listed after the unflagged ones.",
      "Fine for internal or personal posts; for a client, pick an unflagged sticker instead.",
    ],
  },
  {
    key: "gif-picker-ip-risk-notice",
    title: "Licensed content added",
    body: "You added a GIF or sticker flagged as a licensed character or a celebrity. Nothing is blocked — but the post will show a warning in the pre-publish check and on the approval card. Swap it for an unflagged one if this is for a client.",
  },
  {
    key: "video-editor-sticker-ip-risk",
    title: "Licensed character / Celebrity sticker",
    body: "This sticker shows a studio character or a celebrity. Studio/celebrity content — avoid in client posts. If you keep it, the post will carry a pre-publish and approval warning.",
  },
  {
    key: "video-editor-run-qa",
    title: "Run QA",
    body: "Runs an automated pass over this version — platform spec checks plus an AI visual review for things like clipped captions or a static open. Findings show up as QA comments below, timestamped to the moment they happen.",
  },
  {
    key: "video-editor-qa-claims",
    title: "Testimonial / claims declarations",
    body: "Tell QA what it can't see in the pixels: results disclaimers, substantiation, whether the claim is spoken, and the client's HIPAA authorization. Tick \"contains a testimonial\" and QA checks them against the FTC Endorsement Guides and HIPAA rules.",
    bullets: [
      "Open by default for HIPAA-covered clients.",
      "Leave a field blank if it doesn't apply — blanks aren't sent.",
    ],
  },
  {
    key: "video-editor-tighten",
    title: "Tighten",
    body: "Finds dead air and filler words ('um', 'uh'...) in the selected clip's source and shows you exactly what it would cut before touching anything — review the list, then Apply as one undoable edit or Discard.",
  },
  {
    key: "quick-reel-transcript-picker",
    title: "Pick From the Transcript",
    body: "Instead of dragging trim handles, tap the lines of the spoken transcript you want and the reel is cut to just those moments.",
    bullets: [
      "Only clips from the footage library have a transcript — a freshly uploaded file doesn't yet.",
      "Selected lines play in order, so you can stitch together the best few sentences from a longer clip.",
    ],
  },
];
